/**
 * The way through a vulnerability case (DEV-103), pure and React-free.
 *
 * Main path: 1 core facts → 2 Security Advisory (released) → 3 customer
 * mails. The mails need the released advisory; the advisory takes the core
 * facts. Exactly one step is „next“: the first one not done. Beside the path
 * run the reports to the authority, driven by their deadlines.
 */
import { t } from '@lingui/core/macro';

import type { CaseFacts, VulnerabilityCase } from '../../lib/types';

import { clockUrgency } from './clockUrgency';

export type CaseSection = 'reports' | 'facts' | 'advisory' | 'mails';
export type StepState = 'done' | 'next' | 'open' | 'locked';

export interface CaseStep {
  section: Exclude<CaseSection, 'reports'>;
  number: number;
  label: string;
  state: StepState;
  /** One short line: what stands, or why the step waits. */
  detail: string;
}

/** The core facts the advisory and the reports draw on. */
export const FACT_KEYS: readonly (keyof CaseFacts)[] = [
  'summary', 'exploitation_details', 'impact', 'user_mitigations', 'corrective_measures',
];

type CaseInput = Pick<VulnerabilityCase, 'facts' | 'advisory' | 'mails' | 'status'>;

function factsStep(item: CaseInput): Omit<CaseStep, 'state'> & { done: boolean } {
  const open = FACT_KEYS.filter((key) => !String(item.facts[key] ?? '').trim()).length;
  const total = FACT_KEYS.length;
  return {
    section: 'facts', number: 1, label: t`Kernangaben`, done: open === 0,
    detail: open === 0 ? t`vollständig` : t`${open} von ${total} offen`,
  };
}

function advisoryStep(item: CaseInput): Omit<CaseStep, 'state'> & { done: boolean } {
  const released = item.advisory.status === 'RELEASED';
  return {
    section: 'advisory', number: 2, label: t`Security Advisory`, done: released,
    detail: released ? t`freigegeben` : item.advisory.status === 'IN_PROGRESS' ? t`in Arbeit` : t`noch nicht freigegeben`,
  };
}

function mailsDetail(released: boolean, total: number, sent: number): string {
  if (!released) return t`erst Advisory freigeben`;
  if (total === 0) return t`keine Kundenmails`;
  return sent === total ? t`alle ${total} versendet` : t`${sent} von ${total} versendet`;
}

/** The three steps of the main path with their state. */
export function caseSteps(item: CaseInput): CaseStep[] {
  const facts = factsStep(item);
  const advisory = advisoryStep(item);
  const { total, sent } = item.mails;
  const steps = [facts, advisory, {
    section: 'mails' as const, number: 3, label: t`Kundenmails`, done: advisory.done && sent === total,
    detail: mailsDetail(advisory.done, total, sent),
  }];
  const nextIndex = item.status === 'OBSOLETE' ? -1 : steps.findIndex((step) => !step.done);
  return steps.map(({ done, ...step }, index) => {
    let state: StepState = 'open';
    if (done) state = 'done';
    else if (index === nextIndex) state = 'next';
    else if (step.section === 'mails' && !advisory.done) state = 'locked';
    return { ...step, state };
  });
}

/**
 * Where the case opens: at the reports while a deadline runs out within a day,
 * otherwise at the next step of the path.
 */
export function defaultSection(item: CaseInput & Pick<VulnerabilityCase, 'clocks'>): CaseSection {
  if (item.clocks.some((clock) => clockUrgency(clock) >= 3)) return 'reports';
  return caseSteps(item).find((step) => step.state === 'next')?.section ?? 'reports';
}
